import React from 'react';

/**
 * The one row grid of the sidebar (Honey graphite): 30px rows, a 20px icon
 * slot, the label at 38px, counts on the right. Pages, bots and projects use
 * the same parts, so they line up and look like siblings.
 */
export const rowClass = (active, extra = '') =>
  `group/row relative flex items-center gap-2.5 h-[30px] px-2 rounded-lg text-[13px] transition-colors ${
    active ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
  } ${extra}`;

export const IconSlot = ({ children, className = '' }) => (
  <span className={`w-5 h-5 flex-shrink-0 flex items-center justify-center ${className}`}>{children}</span>
);

/** A neutral count (7 tasks). Attention uses AttentionPill. */
export const CountPill = ({ children, className = '' }) => (
  <span className={`ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-accent text-muted-foreground text-[11px] leading-none tabular-nums flex items-center justify-center ${className}`}>
    {children}
  </span>
);

/** The team after a name in a row (a team bot, a team project among the favorites). */
export const teamSuffixClass = 'font-normal text-[11px] text-muted-foreground/60';

/** Something waits for the person (approvals): orange, never red. */
export const AttentionPill = ({ children, title }) => (
  <span title={title} className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-attention-soft text-orange-600 dark:text-attention-text text-[11px] font-medium leading-none tabular-nums flex items-center justify-center">
    {children}
  </span>
);

export const SectionHeader = ({ children, actions, className = '', ...rest }) => (
  <div className={`group/section h-6 flex items-center justify-between px-2 text-[11px] font-medium text-muted-foreground/80 ${className}`} {...rest}>
    <span className="min-w-0 truncate flex items-center gap-1.5">{children}</span>
    {actions && <span className="flex items-center gap-0.5 flex-shrink-0">{actions}</span>}
  </div>
);

export const SectionButton = ({ onClick, title, children, visible = false }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title}
    className={`w-5 h-5 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-opacity ${
      visible ? 'opacity-100' : 'opacity-0 group-hover/section:opacity-100 focus-visible:opacity-100'}`}>
    {children}
  </button>
);

const hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return [59, 130, 246];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const tint = (hex, alpha) => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** A project in a list with its name next to it: a small square in its color. */
export const ProjectDot = ({ project, size = 8, className = '' }) => (
  <span className={`inline-block flex-shrink-0 ${className}`}
    style={{ width: size, height: size, borderRadius: Math.max(2, Math.round(size * 0.35)), background: project?.color || '#3B82F6' }} />
);

/** A project: a rounded square in its color with its first letter. */
export const ProjectTile = ({ project, size = 18, className = '' }) => {
  const color = project?.color || '#3B82F6';
  const letter = ((project?.name || '?').trim()[0] || '?').toUpperCase();
  return (
    <span className={`flex-shrink-0 font-semibold flex items-center justify-center ${className}`}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.28), fontSize: Math.round(size * (size > 28 ? 0.42 : 0.55)), background: tint(color, 0.18), color }}>
      {letter}
    </span>
  );
};
